import React, { useState } from "react";
import { Menu } from "lucide-react";

import { Button } from "@/components/ui/button";

import { GlobalCommandPalette } from "./GlobalCommandPalette";
import { Sidebar } from "./Sidebar";

// ----------------------------------------------------------------------------

interface DashboardLayoutProps {
  children: React.ReactNode;
  className?: string;
}

export function DashboardLayout(props: DashboardLayoutProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { children, className } = props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  function handleToggleSidebar() {
    setIsSidebarOpen((value) => !value);
  }

  function handleCloseSidebar() {
    setIsSidebarOpen(false);
  }

  const contentContainerClassName = "w-full max-w-screen-2xl mx-auto";

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  return (
    <div className={`min-h-screen bg-white ${className || ""}`}>
      <div className="lg:hidden border-b border-gray-200 bg-white">
        <div className="flex items-center justify-between px-4 py-3">
          <a
            href="https://www.gealuce.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleCloseSidebar}
            className="flex items-center gap-2 hover:opacity-90 transition-opacity"
          >
            <div className="w-7 h-7 bg-[#E9E21B] rounded flex items-center justify-center font-bold text-base">
              G
            </div>
            <span className="font-bold text-gray-900">Gea Luce</span>
          </a>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            onClick={handleToggleSidebar}
          >
            <Menu className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {isSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={handleCloseSidebar}
        />
      )}

      <div className="flex min-h-screen">
        <div
          className={`fixed inset-y-0 left-0 z-50 w-64 bg-gray-50 transition-transform duration-200 lg:static lg:translate-x-0 lg:z-auto ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <Sidebar
            onNavigate={handleCloseSidebar}
            onClose={handleCloseSidebar}
          />
        </div>

        <main className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
            <div className={`${contentContainerClassName} space-y-6`}>
              <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
                <GlobalCommandPalette />
              </div>
              <div>{children}</div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
